import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, Send, Trash2 } from "lucide-react";
import {
  Badge,
  Button,
  FormField,
  Input,
} from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  collectionPackagesApi,
  externalRecipientsApi,
  type CollectionPackageDto,
  type ExternalRecipientDto,
} from "@/api/collectionPackages";
import {
  MATERIAL_AUDIENCES,
  collectionTemplatesApi,
  type CollectionTemplateDto,
  type MaterialAudience,
} from "@/api/collectionTemplates";
import { collectionBundlesApi } from "@/api/collectionBundles";
import {
  LETTER_SYNC_KEYS,
  conditionalApprovalLettersApi,
  isBorrowerLetter,
  isConditionalApprovalLetter,
  isRecapLetter,
  isSellerLetter,
  letterTemplateAllowed,
  type ActiveSellerLetterDto,
} from "@/api/conditionalApprovalLetters";
import { conditionalApprovalLetterKeys } from "@/hooks/collection/conditionalApprovalLetterKeys";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { portalAccountsApi } from "@/api/portalAccounts";
import {
  OwnerPicker,
  type OwnerEntityType,
  type OwnerSelection,
} from "@/components/Collection/OwnerPicker";
import { RecipientPicker } from "@/components/Collection/RecipientPicker";
import { firstCompatibleOwner } from "@/components/Collection/portalAccountOwner";
import { TemplateKindBadge } from "@/components/Collection/TemplateKindBadge";
import {
  SIGNER_FIELDS_MISSING_MESSAGE,
  SenderDetailsSection,
  missingSignerFields,
  templateHasSenderFields,
  type SenderValue,
  type SenderValues,
} from "@/components/Collection/SenderDetailsSection";
import { VendorCompensationEditor } from "@/components/Collection/VendorCompensationEditor";
import { usePortalInvitation } from "@/hooks/collection/usePortalInvitation";
import { useOptionalAuth } from "@/hooks/useAuth";
import { calendarDateInputToIso } from "@/utils/datetime";
import { ApprovalChainComposerPanel } from "@/components/Collection/ApprovalChain/ApprovalChainComposerPanel";
import {
  SendBlockedList,
  sendBlockedProblems,
  useChainReadiness,
} from "@/components/Collection/ApprovalChain/chainSend";
import { invalidateApprovalChainReads } from "@/hooks/collection/approvalChainKeys";

// External Intake & Collection — Account-First Phase 3. Account leads,
// items follow, owner is a (mostly auto-filled) filing detail at the
// end. Replaces the prior owner-first ordering. The standalone signup-
// link page is still wired for create-an-account-without-a-package; the
// "Assign more groups" action on PortalAccountDetailPage now routes here
// with ?focus=bundle to expand the bundle picker.

// WS-B — the Vendor Engagement & Compensation Schedule letter. When it is in
// the composer it gets a bespoke compensation editor in addition to its
// Section-1 sender fields.
//
// Step 6 W5 — matched on the DOCUMENT TAG, not the template GUID. Editing a
// collection template copy-on-edits it into a brand-new GUID, so the old
// literal-id match worked until the first admin edit and then silently
// unmounted this editor: staff would get the generic form renderer, no
// {{CompensationSchedule}} block would be assembled, and nobody would find
// out until a vendor countersigned a letter with a blank compensation
// section. The tag carries forward across versions.
const VENDOR_COMPENSATION_SCHEDULE_TAG = "VendorCompensationSchedule";

function isCompensationScheduleTemplate(t: CollectionTemplateDto | undefined): boolean {
  return t?.documentTagCode === VENDOR_COMPENSATION_SCHEDULE_TAG;
}

/** Merge two SavedFormDataJson sources for one item (Section-1 sender values
 *  + the compensation editor's structured values + assembled block). Keys are
 *  disjoint; sender values come first so a compensation key never silently
 *  loses to a sender one. Returns null when both are empty. */
function mergeItemJson(
  senderJson: string | undefined,
  compensationJson: string | undefined,
): string | null {
  const parse = (blob: string | undefined): Record<string, unknown> => {
    if (!blob || !blob.trim()) return {};
    try {
      const parsed = JSON.parse(blob) as unknown;
      return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  };
  const sender = parse(senderJson);
  const compensation = parse(compensationJson);
  if (Object.keys(sender).length === 0 && Object.keys(compensation).length === 0) {
    return null;
  }
  return JSON.stringify({ ...sender, ...compensation });
}

export function CollectionPackageNewPage() {
  useBreadcrumbs([
    { label: "External Collection" },
    { label: "Accounts", href: "/admin/portal-accounts" },
    { label: "New request" },
  ]);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [params] = useSearchParams();
  const auth = useOptionalAuth();
  const user = auth?.user ?? null;
  const hasPermission = (perm: string) => auth?.hasPermission?.(perm) ?? false;

  const prefilledOwner: OwnerSelection | null = useMemo(() => {
    const t = params.get("ownerEntityType");
    const id = params.get("ownerEntityId");
    const name = params.get("ownerDisplayName");
    if (!t || !id) return null;
    return {
      ownerEntityType: t as OwnerEntityType,
      ownerEntityId: id,
      ownerDisplayName: name ?? `${t} ${id.slice(0, 8)}`,
    };
  }, [params]);

  // FL-10 — `?accountType=` pre-locks the party-role field. Coming from
  // PortalAccountDetailPage's "Send another request" / "Assign more
  // groups" actions.
  const prefilledAccountType = useMemo(() => {
    const t = params.get("accountType");
    if (!t) return null;
    if (!MATERIAL_AUDIENCES.includes(t as MaterialAudience)) return null;
    if (t === "All") return null;
    return t as MaterialAudience;
  }, [params]);

  const prefilledEmail = params.get("email") ?? "";
  const prefilledRecipientId = params.get("recipientId");
  const focusOnBundle = params.get("focus") === "bundle";

  const [recipient, setRecipient] = useState<ExternalRecipientDto | null>(null);
  const [roleLabel, setRoleLabel] = useState("");
  const [partyRole, setPartyRole] = useState<MaterialAudience | "">(
    prefilledAccountType ?? "",
  );
  const [owner, setOwner] = useState<OwnerSelection | null>(prefilledOwner);
  const [ownerChangeMode, setOwnerChangeMode] = useState(false);

  // One email = one account = one party type (Tier 2 #9). The party-role
  // field locks whenever the choice is upstream-determined — either via
  // ?accountType= or by the linked PortalUser on the picked recipient.
  const lockedAccountType: MaterialAudience | null =
    prefilledAccountType ?? recipient?.linkedAccountType ?? null;

  useEffect(() => {
    if (lockedAccountType && partyRole !== lockedAccountType) {
      setPartyRole(lockedAccountType);
    }
  }, [lockedAccountType, partyRole]);

  // Pre-bind the recipient when arriving from PortalAccountDetailPage.
  useEffect(() => {
    if (!prefilledRecipientId || recipient) return;
    let cancelled = false;
    void externalRecipientsApi
      .get(prefilledRecipientId)
      .then((r) => {
        if (!cancelled) setRecipient(r);
      })
      .catch(() => {
        // Silent fall-through — staff can still look up by email manually.
      });
    return () => {
      cancelled = true;
    };
  }, [prefilledRecipientId, recipient]);

  // Phase 3 — fetch the account's attachments once the recipient resolves
  // so we can default the "Filing detail" owner to the first compatible
  // attachment. The query is skipped when the recipient has no linked
  // PortalUser (legacy / pre-Tier-2 rows) or when the staff already
  // arrived with an owner via query param.
  const accountId = recipient?.portalUserId ?? null;
  const accountQuery = useQuery({
    queryKey: ["portal-account", accountId],
    queryFn: () => portalAccountsApi.get(accountId!),
    enabled: Boolean(accountId),
  });

  useEffect(() => {
    // Only auto-fill when staff hasn't picked an owner and isn't already
    // in the middle of changing one.
    if (owner || ownerChangeMode) return;
    if (!accountQuery.data) return;
    const defaulted = firstCompatibleOwner(accountQuery.data.attachments);
    if (defaulted) setOwner(defaulted);
  }, [accountQuery.data, owner, ownerChangeMode]);

  const [dueDate, setDueDate] = useState("");
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [templateToAdd, setTemplateToAdd] = useState("");
  // PR 2 (E) — multi-pick bundle picker. Staff used to add one bundle
  // at a time through a Select+Add; the checkbox row lets a liaison
  // assemble a multi-group package in one motion.
  const [selectedBundleIds, setSelectedBundleIds] = useState<string[]>([]);
  // Sender-Fields v1 — captured sender/letter values per template id (JSON),
  // seeded into the item's SavedFormDataJson at create.
  const [senderValues, setSenderValues] = useState<Record<string, string>>({});
  const handleSenderValuesChange = useCallback((templateId: string, valuesJson: string) => {
    setSenderValues((prev) =>
      prev[templateId] === valuesJson ? prev : { ...prev, [templateId]: valuesJson },
    );
  }, []);
  // WS-B — captured compensation values per template id (JSON). Stored
  // separately from senderValues and merged with them at create.
  const [compensationValues, setCompensationValues] = useState<Record<string, string>>({});
  const handleCompensationChange = useCallback((templateId: string, valuesJson: string) => {
    setCompensationValues((prev) =>
      prev[templateId] === valuesJson ? prev : { ...prev, [templateId]: valuesJson },
    );
  }, []);

  // Sender-Fields v1 — the deal owner (when present) drives prefill; Owner
  // Recapitalization — it also decides which Conditional Approval letters the
  // picker offers and which bundles are pre-checked.
  const dealId =
    owner?.ownerEntityType === "Deal" ? owner.ownerEntityId : null;

  const templatesQuery = useQuery({
    // Tier 2 #2 — narrow the template picker by the structured role.
    queryKey: ["collection-templates", "active", partyRole || null],
    queryFn: () => collectionTemplatesApi.list(false, partyRole || null),
  });
  const templates: CollectionTemplateDto[] = templatesQuery.data ?? [];

  // Owner Recapitalization (letters.json composer_notes 0, 14–16) — the letters
  // this deal may send, the preselected Seller letter and the deal's active
  // Seller letter. The letters route to the deal's parties through their chain,
  // so they are offered whatever the recipient's party type: the whole active
  // catalog is read to resolve them.
  const letterOptionsQuery = useQuery({
    queryKey: conditionalApprovalLetterKeys.options(dealId),
    queryFn: () => conditionalApprovalLettersApi.options(dealId!),
    enabled: Boolean(dealId),
  });
  const letterOptions = letterOptionsQuery.data;
  const allTemplatesQuery = useQuery({
    queryKey: conditionalApprovalLetterKeys.allActiveTemplates,
    queryFn: () => collectionTemplatesApi.list(false, null),
    enabled: Boolean(dealId),
  });
  const templatesById = useMemo(() => {
    const map = new Map(templates.map((t) => [t.id, t] as const));
    for (const t of allTemplatesQuery.data ?? []) {
      if (!map.has(t.id) && isConditionalApprovalLetter(t.lineageId)) map.set(t.id, t);
    }
    return map;
  }, [templates, allTemplatesQuery.data]);
  // The picker: every template the deal may send — today's list, with a
  // Conditional Approval letter only where the deal's options allow it, plus
  // the deal's letters whatever their audience.
  const pickerTemplates = useMemo(() => {
    const offered = new Map<string, CollectionTemplateDto>();
    for (const t of templates) {
      if (letterTemplateAllowed(t.lineageId, dealId, letterOptions)) offered.set(t.id, t);
    }
    for (const option of letterOptions?.letters ?? []) {
      const t = templatesById.get(option.templateId);
      if (t && !offered.has(t.id)) offered.set(t.id, t);
    }
    return [...offered.values()];
  }, [templates, templatesById, dealId, letterOptions]);

  const bundlesQuery = useQuery({
    // Owner Recapitalization (D7, Q8) — read for the deal: the server hides the
    // recap bundles on an Acquisition deal and flags them on a recap deal.
    queryKey: conditionalApprovalLetterKeys.composerBundles(partyRole || null, dealId),
    queryFn: () => collectionBundlesApi.list(false, partyRole || null, dealId),
  });
  const bundles = bundlesQuery.data ?? [];

  // Pre-check the bundles suggested for this deal, once per deal and party type
  // (staff may uncheck them; the pre-check never re-applies over that).
  const [prechecked, setPrechecked] = useState<string | null>(null);
  const precheckKey = `${dealId ?? ""}|${partyRole || ""}`;
  useEffect(() => {
    if (!dealId || !bundlesQuery.data || prechecked === precheckKey) return;
    setPrechecked(precheckKey);
    const suggested = bundlesQuery.data.filter((b) => b.suggestedForDeal).map((b) => b.id);
    if (suggested.length === 0) return;
    setSelectedBundleIds((prev) => [...prev, ...suggested.filter((id) => !prev.includes(id))]);
  }, [dealId, bundlesQuery.data, prechecked, precheckKey]);

  // composer_notes 3, 5 — the Base Financed Amount and the Note Holder signer
  // entered on one recap letter are copied to the other letter of this send.
  const [letterSync, setLetterSync] = useState<SenderValues>({});
  const handleLetterSync = useCallback((name: string, value: SenderValue) => {
    setLetterSync((prev) => (prev[name] === value ? prev : { ...prev, [name]: value }));
  }, []);

  // composer_notes 16 — one Seller letter per deal: switching asks first; a
  // Seller letter already sent on the deal must be voided before another.
  const [sellerSwitch, setSellerSwitch] = useState<{ from: string; to: string[] } | null>(null);
  const [sentSellerLetter, setSentSellerLetter] = useState<ActiveSellerLetterDto | null>(null);

  const invitation = usePortalInvitation({
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  // Approval chains (D3/D4) — a selection that routes through a chain is not
  // sent in one step: "Save & send" saves the draft, then the routing review
  // below shows each chain (preflight, attestations, adjustments) and sends.
  const chainTemplateIds = selectedTemplateIds.filter(
    (id) => templatesById.get(id)?.approvalChain != null,
  );
  const hasChainTemplate = chainTemplateIds.length > 0;
  const [routingDraft, setRoutingDraft] = useState<CollectionPackageDto | null>(null);
  const [sendProblems, setSendProblems] = useState<string[]>([]);
  const chainReadiness = useChainReadiness();

  // Every read a save or send changes (shared by both mutations below).
  const invalidatePackageReads = (pkg: CollectionPackageDto) => {
    void qc.invalidateQueries({
      queryKey: [
        "collection-packages",
        pkg.ownerEntityType,
        pkg.ownerEntityId,
      ],
    });
    // Account-scoped surfaces by prefix: accountId can be null (legacy
    // recipient) and a send can mint a brand-new portal account, so the
    // per-id key wouldn't reach them. These queries are inactive here —
    // prefix invalidation only stale-marks them.
    void qc.invalidateQueries({ queryKey: ["portal-account-packages"] });
    void qc.invalidateQueries({ queryKey: ["portal-account"] });
    void qc.invalidateQueries({ queryKey: ["portal-accounts"] });
    void qc.invalidateQueries({
      queryKey: ["external-recipient", pkg.recipientId],
    });
  };

  const savePackage = useMutation<
    CollectionPackageDto,
    Error,
    { send: boolean; routing?: boolean }
  >({
    mutationFn: async ({ send, routing }) => {
      if (!recipient) throw new Error("Pick an external account before saving.");
      if (!owner)
        throw new Error(
          "Pick a filing destination before saving — none of this account's attached records was eligible.",
        );
      if (selectedTemplateIds.length === 0)
        throw new Error("Add at least one item before saving.");

      // Tier 2 #9 — party type is required at invitation-issue time.
      if (send && !partyRole) {
        throw new Error(
          "Pick a party type (Buyer / Seller / Vendor / Broker) before sending.",
        );
      }

      const created = await collectionPackagesApi.create({
        ownerEntityType: owner.ownerEntityType,
        ownerEntityId: owner.ownerEntityId,
        recipientId: recipient.id,
        recipientRoleLabel: roleLabel.trim() || null,
        // PROB-05 — calendar date sent at noon UTC so a TZ shift can't
        // flip it back a day on render.
        dueDate: calendarDateInputToIso(dueDate),
      });

      for (const templateId of selectedTemplateIds) {
        // WS-B — the Vendor Engagement & Compensation Schedule item carries
        // BOTH Section-1 sender values and the compensation editor's values;
        // merge them into one SavedFormDataJson. All other items pass their
        // sender values unchanged.
        const savedFormDataJson = isCompensationScheduleTemplate(
          templatesById.get(templateId),
        )
          ? mergeItemJson(senderValues[templateId], compensationValues[templateId])
          : senderValues[templateId] ?? null;
        await collectionPackagesApi.addItem(created.id, templateId, savedFormDataJson);
      }

      if (send && !routing) {
        const sent = await collectionPackagesApi.send(created.id);
        await invitation.mutateAsync({
          packageId: created.id,
          accountType: partyRole as MaterialAudience,
        });
        return sent;
      }
      return collectionPackagesApi.get(created.id);
    },
    onSuccess: (pkg, vars) => {
      invalidatePackageReads(pkg);
      if (vars.routing) {
        // Stay here: the routing review below sends the draft.
        invalidateApprovalChainReads(qc);
        setRoutingDraft(pkg);
        toast.success("Draft saved — review the approval chain, then send.");
        return;
      }
      toast.success(
        vars.send ? "Package sent — invitation emailed." : "Draft saved.",
      );
      navigate(`/collection/packages/${pkg.id}`);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  // Sends the draft the routing review shows. A 422 is the server refusing the
  // whole send (nothing goes out partially) — list every problem it names.
  const sendRoutingDraft = useMutation<CollectionPackageDto, Error, CollectionPackageDto>({
    mutationFn: async (draft) => {
      setSendProblems([]);
      const sent = await collectionPackagesApi.send(draft.id);
      await invitation.mutateAsync({
        packageId: draft.id,
        accountType: partyRole as MaterialAudience,
      });
      return sent;
    },
    onSuccess: (pkg) => {
      invalidatePackageReads(pkg);
      invalidateApprovalChainReads(qc);
      toast.success("Package sent — invitation emailed.");
      navigate(`/collection/packages/${pkg.id}`);
    },
    onError: (e) => {
      const problems = sendBlockedProblems(e);
      if (problems) setSendProblems(problems);
      else toast.error(extractErrorMessage(e));
      invalidateApprovalChainReads(qc);
    },
  });

  const canSubmit =
    Boolean(recipient) && Boolean(owner) && selectedTemplateIds.length > 0;

  // Sender-Fields v1 — selected templates that carry sender/letter fields get
  // a "Sender details" step; the deal owner (when present) drives prefill.
  const senderTemplates = selectedTemplateIds
    .map((id) => templatesById.get(id))
    .filter(
      (t): t is CollectionTemplateDto =>
        !!t && templateHasSenderFields(t.formSchemaJson),
    );
  // WS-B — whether the Vendor Engagement & Compensation Schedule letter is in
  // the package; it gets the bespoke compensation editor (its sender-field
  // schema already lands it in senderTemplates, but the flag keeps the
  // "Sender details" section + editor visible even if that ever changes).
  const compensationTemplate = selectedTemplateIds
    .map((id) => templatesById.get(id))
    .find(isCompensationScheduleTemplate);
  const hasVendorCompensationTemplate = compensationTemplate !== undefined;

  // composer_notes 4/5 — the send is held until each recap letter names its
  // signers.
  const signerFieldsMissing = senderTemplates.some(
    (t) => isRecapLetter(t.lineageId) && missingSignerFields(t.formSchemaJson, senderValues[t.id]).length > 0,
  );

  function addTemplate() {
    if (!templateToAdd) return;
    if (selectedTemplateIds.includes(templateToAdd)) return;
    const t = templatesById.get(templateToAdd);
    setTemplateToAdd("");
    if (!t || !isConditionalApprovalLetter(t.lineageId)) {
      setSelectedTemplateIds((prev) => [...prev, templateToAdd]);
      return;
    }
    requestLetters(lettersToAdd(t));
  }

  /** composer_notes 0, 14 — on an Owner Recapitalization deal a requested
   *  letter brings the pair: the Borrower letter and the preselected Seller
   *  letter (or the Seller letter staff picked). On an Acquisition deal only
   *  the picked letter is added (nothing is preselected by election). */
  function lettersToAdd(t: CollectionTemplateDto): string[] {
    const ids = [t.id];
    if (letterOptions?.dealType !== "OwnerRecapitalization") return ids;
    const selectedLetters = selectedTemplateIds.map((id) => templatesById.get(id)?.lineageId);
    if (isSellerLetter(t.lineageId)) {
      const borrower = letterOptions.letters.find((l) => l.party === "Borrower" && l.preselected);
      if (borrower && !selectedLetters.some((l) => isBorrowerLetter(l))) ids.unshift(borrower.templateId);
    } else {
      const seller = letterOptions.letters.find((l) => l.party === "Seller" && l.preselected);
      if (seller && !selectedLetters.some((l) => isSellerLetter(l))) ids.push(seller.templateId);
    }
    return ids;
  }

  function requestLetters(ids: string[]) {
    const incoming = ids.filter((id) => !selectedTemplateIds.includes(id));
    const incomingSeller = incoming.find((id) => isSellerLetter(templatesById.get(id)?.lineageId));
    if (incomingSeller && letterOptions?.activeSellerLetter) {
      setSentSellerLetter(letterOptions.activeSellerLetter);
      return;
    }
    const currentSeller = selectedTemplateIds.find((id) => isSellerLetter(templatesById.get(id)?.lineageId));
    if (incomingSeller && currentSeller) {
      setSellerSwitch({ from: currentSeller, to: incoming });
      return;
    }
    setSelectedTemplateIds((prev) => [...prev, ...incoming.filter((id) => !prev.includes(id))]);
  }

  /** composer_notes 0 — the deal's preselected letters in one step. */
  function addPreselectedLetters() {
    const ids = (letterOptions?.letters ?? []).filter((l) => l.preselected).map((l) => l.templateId);
    if (ids.length > 0) requestLetters(ids);
  }

  function addSelectedBundles() {
    if (selectedBundleIds.length === 0) return;
    const incoming: string[] = [];
    for (const id of selectedBundleIds) {
      const bundle = bundles.find((b) => b.id === id);
      if (!bundle) continue;
      const ordered = bundle.slots
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((s) => s.templateId);
      incoming.push(...ordered);
    }
    setSelectedTemplateIds((prev) => {
      const seen = new Set(prev);
      const next = prev.slice();
      for (const id of incoming) {
        if (!seen.has(id)) {
          next.push(id);
          seen.add(id);
        }
      }
      return next;
    });
    setSelectedBundleIds([]);
  }

  if (routingDraft) {
    const routedItems = routingDraft.items.filter(
      (i) => i.approvalChainStatus != null || templatesById.get(i.templateId)?.approvalChain != null,
    );
    const ready = chainReadiness.summarize(routedItems.map((i) => i.id));
    const dealOwnerId =
      routingDraft.ownerEntityType === "Deal" ? routingDraft.ownerEntityId : null;
    return (
      <div className="flex flex-col gap-space-4">
        <PageHeader
          title="Review the approval chain"
          description="This request is saved as a draft. Check who will receive each routed document, confirm what is required, then send."
        />
        <p className="text-caption text-gray-mid m-0">
          To change the items or the recipient,{" "}
          <Link
            to={`/collection/packages/${routingDraft.id}`}
            className="text-gold-shadow hover:underline"
          >
            open the draft package
          </Link>
          .
        </p>
        {routedItems.map((item) => (
          <ApprovalChainComposerPanel
            key={item.id}
            packageId={routingDraft.id}
            itemId={item.id}
            itemName={item.templateName || templatesById.get(item.templateId)?.name || "this item"}
            dealId={dealOwnerId}
            hasPermission={hasPermission}
            onReadinessChange={chainReadiness.report}
          />
        ))}
        <SendBlockedList
          title="Send is held until the approval chain is ready:"
          problems={ready.reasons}
          testId="chain-send-held"
        />
        <SendBlockedList
          title="The package was not sent. Fix each problem, then send again:"
          problems={sendProblems}
          testId="send-blocked-errors"
        />
        <div className="flex items-center justify-end gap-space-3">
          <Button
            type="button"
            onClick={() => sendRoutingDraft.mutate(routingDraft)}
            disabled={!ready.ready || sendRoutingDraft.isPending}
            loading={sendRoutingDraft.isPending}
            data-testid="routing-send"
          >
            <Send size={14} aria-hidden />
            Send
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-4">
      <Link
        to="/admin/portal-accounts"
        className="inline-flex items-center gap-space-1 text-caption text-gold-shadow hover:text-gold"
      >
        <ArrowLeft size={12} aria-hidden /> Back to External Collection
      </Link>

      <PageHeader
        title="New document request"
        description="Start with the external account. Items follow; the filing destination defaults to one of the account's attached records."
      />

      <section className="rounded-md border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-3">
        <h2 className="text-small font-semibold text-onyx m-0">
          1. External account
        </h2>
        <RecipientPicker
          value={recipient}
          onChange={setRecipient}
          initialEmail={prefilledEmail}
        />
        {recipient && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
            <FormField
              label="Party type"
              htmlFor="party-role"
              helperText={
                lockedAccountType
                  ? `Locked to the existing ${lockedAccountType} account for this email — one email = one party type.`
                  : "Routing key for bundles, audience-tagged templates, and per-party dashboards."
              }
            >
              <Select
                id="party-role"
                value={partyRole}
                onChange={(e) =>
                  setPartyRole(e.target.value as MaterialAudience | "")
                }
                disabled={!!lockedAccountType}
              >
                <option value="">Unspecified</option>
                {MATERIAL_AUDIENCES.filter((a) => a !== "All").map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField
              label="Recipient role label"
              htmlFor="role-label"
              helperText="Free-text label shown on the portal — e.g. Guarantor, Vendor contact."
            >
              <Input
                id="role-label"
                value={roleLabel}
                onChange={(e) => setRoleLabel(e.target.value)}
                // PROB-03 — placeholder echoes the picked party type so a
                // Seller account doesn't see "Buyer" hovering over the
                // field; falls back to a generic example when unset.
                placeholder={
                  partyRole && partyRole !== "All"
                    ? partyRole
                    : "e.g. Buyer / Seller / Guarantor"
                }
              />
            </FormField>
          </div>
        )}
      </section>

      <section className="rounded-md border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-3">
        <h2 className="text-small font-semibold text-onyx m-0">2. Items</h2>

        {/* Bundle picker first when the composer was opened via "Assign
            more groups" — the natural primary action there is picking a
            group, not an individual template. */}
        {focusOnBundle && (
          <p className="text-caption text-gray-mid">
            Adding more bundles to this account.
          </p>
        )}

        {focusOnBundle ? (
          <>
            {renderBundleRow()}
            {renderTemplateRow()}
          </>
        ) : (
          <>
            {renderTemplateRow()}
            {renderBundleRow()}
          </>
        )}
        {renderLetterRow()}
        {renderDueRow()}

        {selectedTemplateIds.length === 0 ? (
          <p className="text-caption text-gray-mid italic">
            No items yet. Add at least one before saving.
          </p>
        ) : (
          <ul className="divide-y divide-border-light border border-border-light rounded-md m-0 p-0 list-none">
            {selectedTemplateIds.map((id) => {
              const t = templatesById.get(id);
              return (
                <li
                  key={id}
                  className="flex items-center justify-between gap-space-3 p-space-3"
                >
                  <div className="flex items-center gap-space-2 min-w-0">
                    <TemplateKindBadge
                      kind={t?.kind ?? "—"}
                      isSpecialForm={t?.isSpecialForm}
                    />
                    <span className="text-small text-onyx truncate">
                      {t?.name ?? id}
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="tertiary"
                    size="sm"
                    onClick={() =>
                      setSelectedTemplateIds((prev) =>
                        prev.filter((x) => x !== id),
                      )
                    }
                  >
                    <Trash2 size={14} aria-hidden />
                    Remove
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {(senderTemplates.length > 0 || hasVendorCompensationTemplate) && (
        <section className="rounded-md border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-4">
          <h2 className="text-small font-semibold text-onyx m-0">
            3. Sender details
          </h2>
          {senderTemplates.map((t) => (
            <div key={t.id} className="flex flex-col gap-space-4">
              <SenderDetailsSection
                templateId={t.id}
                templateName={t.name}
                formSchemaJson={t.formSchemaJson}
                dealId={dealId}
                recipientName={recipient?.fullName ?? null}
                staffName={user?.displayName ?? null}
                staffEmail={user?.email ?? null}
                onValuesChange={handleSenderValuesChange}
                {...(isRecapLetter(t.lineageId)
                  ? {
                      syncKeys: LETTER_SYNC_KEYS,
                      syncedValues: letterSync,
                      onSyncedValueChange: handleLetterSync,
                    }
                  : {})}
              />
              {isCompensationScheduleTemplate(t) && (
                <VendorCompensationEditor
                  templateId={t.id}
                  templateName={t.name}
                  senderValuesJson={senderValues[t.id]}
                  onValuesChange={handleCompensationChange}
                />
              )}
            </div>
          ))}
          {/* Fallback — the vendor template is selected but carries no
              Section-1 sender fields, so it isn't in senderTemplates. Render
              the compensation editor on its own. */}
          {compensationTemplate &&
            !senderTemplates.some(isCompensationScheduleTemplate) && (
              <VendorCompensationEditor
                templateId={compensationTemplate.id}
                templateName={compensationTemplate.name}
                senderValuesJson={senderValues[compensationTemplate.id]}
                onValuesChange={handleCompensationChange}
              />
            )}
        </section>
      )}

      <section className="rounded-md border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-3">
        <h2 className="text-small font-semibold text-onyx m-0">
          {senderTemplates.length > 0 ? "4. Filing detail" : "3. Filing detail"}
        </h2>
        <p className="text-caption text-gray-mid m-0">
          Where this package files in the DMS. Defaults to one of the
          account's attached records — change if it should land somewhere
          else.
        </p>
        {!ownerChangeMode && owner ? (
          <div className="flex items-center justify-between gap-space-3 rounded-md border border-border-light p-space-3">
            <div className="flex items-center gap-space-2 min-w-0">
              <Badge status="info">{owner.ownerEntityType}</Badge>
              <span className="text-small text-onyx truncate">
                {owner.ownerDisplayName}
              </span>
            </div>
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={() => setOwnerChangeMode(true)}
            >
              Change
            </Button>
          </div>
        ) : (
          <>
            {accountQuery.isLoading && accountId && (
              <p className="text-caption text-gray-mid italic">
                Loading the account's attached records…
              </p>
            )}
            <OwnerPicker value={owner} onChange={setOwner} />
            {ownerChangeMode && owner && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="tertiary"
                  size="sm"
                  onClick={() => setOwnerChangeMode(false)}
                >
                  Done
                </Button>
              </div>
            )}
          </>
        )}
      </section>

      {signerFieldsMissing && (
        <p className="text-small text-status-error m-0 text-right" role="alert" data-testid="signer-fields-missing">
          {SIGNER_FIELDS_MISSING_MESSAGE}
        </p>
      )}
      <ConfirmModal
        open={sellerSwitch !== null}
        title="Replace the Seller letter?"
        message={
          sellerSwitch
            ? `This request already has "${templatesById.get(sellerSwitch.from)?.name ?? "a Seller letter"}". A deal carries one Seller letter. Replace it with "${
                sellerSwitch.to
                  .map((id) => templatesById.get(id))
                  .find((t) => isSellerLetter(t?.lineageId))?.name ?? "the new Seller letter"
              }"?`
            : ""
        }
        confirmLabel="Replace"
        onCancel={() => setSellerSwitch(null)}
        onConfirm={() => {
          const change = sellerSwitch;
          setSellerSwitch(null);
          if (!change) return;
          setSelectedTemplateIds((prev) => [
            ...prev.filter((id) => id !== change.from),
            ...change.to.filter((id) => !prev.includes(id)),
          ]);
        }}
      />
      <ConfirmModal
        open={sentSellerLetter !== null}
        title="Void the first Seller letter"
        message={
          sentSellerLetter
            ? `This deal already has an active Seller letter, "${sentSellerLetter.templateName}". A deal carries one Seller letter: void the first (cancel its approval chain, or archive the item), then add the new one.`
            : ""
        }
        confirmLabel="Open its request"
        cancelLabel="Close"
        onCancel={() => setSentSellerLetter(null)}
        onConfirm={() => {
          const active = sentSellerLetter;
          setSentSellerLetter(null);
          if (active) navigate(`/collection/packages/${active.packageId}`);
        }}
      />

      <div className="flex items-center justify-end gap-space-3">
        <Button
          type="button"
          variant="secondary"
          onClick={() => savePackage.mutate({ send: false })}
          disabled={!canSubmit || savePackage.isPending}
          loading={savePackage.isPending && savePackage.variables?.send === false}
        >
          Save draft
        </Button>
        <Button
          type="button"
          onClick={() => savePackage.mutate({ send: true, routing: hasChainTemplate })}
          disabled={!canSubmit || savePackage.isPending || signerFieldsMissing}
          loading={savePackage.isPending && savePackage.variables?.send === true}
        >
          <Send size={14} aria-hidden />
          {hasChainTemplate ? "Save & review routing" : "Save & send"}
        </Button>
      </div>
    </div>
  );

  function renderTemplateRow() {
    return (
      <div className="flex gap-space-2">
        <Select
          value={templateToAdd}
          onChange={(e) => setTemplateToAdd(e.target.value)}
          disabled={templatesQuery.isLoading}
          aria-label="Add a template"
        >
          <option value="">
            {templatesQuery.isLoading
              ? "Loading templates…"
              : "Select a template…"}
          </option>
          {pickerTemplates
            .filter((t) => t.active && !selectedTemplateIds.includes(t.id))
            .map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} · {t.kind}
              </option>
            ))}
        </Select>
        <Button
          type="button"
          variant="secondary"
          onClick={addTemplate}
          disabled={!templateToAdd}
        >
          <Plus size={14} aria-hidden /> Add
        </Button>
      </div>
    );
  }

  /** Owner Recapitalization — the deal's Conditional Approval letters. */
  function renderLetterRow() {
    if (!dealId || !letterOptions || letterOptions.letters.length === 0) return null;
    const recap = letterOptions.dealType === "OwnerRecapitalization";
    const preselected = letterOptions.letters.filter((l) => l.preselected);
    return (
      <div
        className="flex flex-col gap-space-2 rounded-md border border-border-light p-space-3"
        data-testid="letter-options"
      >
        <span className="text-caption font-medium text-gray-mid">
          Conditional Approval letters — {recap ? "Owner Recapitalization deal" : "Acquisition deal"}
        </span>
        <span className="text-caption text-gray-mid">
          {recap
            ? letterOptions.optionERecorded
              ? "The Existing Business Debt Summary records Option E, so the Option E Seller letter is preselected. You can switch to the standard Seller letter."
              : "The standard Seller letter is preselected. Switch to the Option E Seller letter if an obligation is kept under Option E."
            : "Pick the Seller Conditional Approval or the Seller Conditional Approval — Option E. A deal carries one Seller letter."}
        </span>
        {recap && preselected.length > 0 && (
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={addPreselectedLetters}
              disabled={preselected.every((l) => selectedTemplateIds.includes(l.templateId))}
              data-testid="add-preselected-letters"
            >
              <Plus size={14} aria-hidden /> Add {preselected.map((l) => l.templateName).join(" and ")}
            </Button>
          </div>
        )}
      </div>
    );
  }

  function renderBundleRow() {
    if (bundlesQuery.isLoading) {
      return <p className="text-caption text-gray-mid m-0">Loading bundles…</p>;
    }
    if (bundles.length === 0) {
      return (
        <p className="text-caption text-gray-mid m-0">
          No bundles available for this party type. {" "}
          <Link to="/admin/collection-bundles" className="underline">
            Author one
          </Link>
          .
        </p>
      );
    }
    const toggle = (id: string) =>
      setSelectedBundleIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
    const totalIncoming = selectedBundleIds.reduce((s, id) => {
      const b = bundles.find((x) => x.id === id);
      return s + (b?.slots.length ?? 0);
    }, 0);
    return (
      <fieldset className="flex flex-col gap-space-2 border border-border-light rounded-md p-space-3 m-0">
        <legend className="text-caption font-medium text-gray-mid px-space-2">
          Add from groups (select one or more)
        </legend>
        {bundles.map((b) => {
          const checked = selectedBundleIds.includes(b.id);
          return (
            <label
              key={b.id}
              className="flex items-start gap-space-2 text-small text-onyx m-0"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(b.id)}
              />
              <span className="flex flex-col">
                <span className="font-medium">{b.name}</span>
                <span className="text-caption text-gray-mid">
                  {b.slots.length} item{b.slots.length === 1 ? "" : "s"}
                  {b.audience ? ` · ${b.audience}` : ""}
                  {b.description ? ` · ${b.description}` : ""}
                </span>
              </span>
            </label>
          );
        })}
        <div>
          <Button
            type="button"
            variant="secondary"
            onClick={addSelectedBundles}
            disabled={selectedBundleIds.length === 0}
          >
            <Plus size={14} aria-hidden />{" "}
            {selectedBundleIds.length > 0
              ? `Add ${selectedBundleIds.length} group${selectedBundleIds.length === 1 ? "" : "s"} (${totalIncoming} item${totalIncoming === 1 ? "" : "s"})`
              : "Add selected groups"}
          </Button>
        </div>
      </fieldset>
    );
  }

  function renderDueRow() {
    return (
      <FormField label="Due date" htmlFor="due-date">
        <Input
          id="due-date"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
        />
      </FormField>
    );
  }
}
